import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, AlertCircle, CheckCircle2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import LegalConsentFields from '../components/LegalConsentFields';
import LegalDocumentsModal from '../components/LegalDocumentsModal';

const SignupPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSent, setIsSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalSection, setLegalModalSection] = useState('terms');

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const openLegalModal = (section) => {
    setLegalModalSection(section);
    setLegalModalOpen(true);
  };

  const handleAcceptedLegalChange = (checked) => {
    setAcceptedLegal(checked);
    if (checked) {
      setFieldErrors((prev) => ({ ...prev, acceptedLegal: '' }));
    }
    if (error) setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'email') {
      const trimmedEmail = value.trim();
      if (!trimmedEmail) {
        setFieldErrors((prev) => ({ ...prev, email: 'Email is required.' }));
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        setFieldErrors((prev) => ({ ...prev, email: 'Invalid email format' }));
      } else {
        setFieldErrors((prev) => ({ ...prev, email: '' }));
      }
    } else if (name === 'password') {
      if (!value) {
        setFieldErrors((prev) => ({ ...prev, password: 'Password is required.' }));
      } else if (!isStrongPassword(value)) {
        setFieldErrors((prev) => ({
          ...prev,
          password: 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.',
        }));
      } else {
        setFieldErrors((prev) => ({ ...prev, password: '' }));
      }

      if (formData.confirmPassword) {
        setFieldErrors((prev) => ({
          ...prev,
          confirmPassword: value === formData.confirmPassword ? '' : 'Passwords do not match.',
        }));
      }
    } else if (name === 'confirmPassword') {
      if (!value) {
        setFieldErrors((prev) => ({ ...prev, confirmPassword: 'Confirm password is required.' }));
      } else if (value !== formData.password) {
        setFieldErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match.' }));
      } else {
        setFieldErrors((prev) => ({ ...prev, confirmPassword: '' }));
      }
    } else if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (error) setError('');
  };

  const isStrongPassword = (password) => {
    const hasMinLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    return hasMinLength && hasUpper && hasLower && hasNumber && hasSpecial;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setFieldErrors({});

    const trimmedName = formData.fullName.trim();
    const trimmedEmail = formData.email.trim();
    const password = formData.password;
    const confirmPassword = formData.confirmPassword;

    const nextFieldErrors = {};
    if (!trimmedName) nextFieldErrors.fullName = 'Full name is required.';
    if (!trimmedEmail) nextFieldErrors.email = 'Email is required.';
    if (!password) nextFieldErrors.password = 'Password is required.';
    if (!confirmPassword) nextFieldErrors.confirmPassword = 'Confirm password is required.';
    if (!acceptedLegal) {
      nextFieldErrors.acceptedLegal = 'Please review and Accept the Terms of Use and Privacy Policy to continue.';
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setError(
        nextFieldErrors.acceptedLegal && Object.keys(nextFieldErrors).length === 1
          ? nextFieldErrors.acceptedLegal
          : 'Please complete all required fields.'
      );
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setFieldErrors({ email: 'Invalid email format' });
      setError('Invalid email format');
      setLoading(false);
      return;
    }

    if (!isStrongPassword(password)) {
      setFieldErrors({ password: 'Password must meet strength requirements.' });
      setError('Password must be at least 8 characters and include uppercase, lowercase, number, and special character.');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setFieldErrors({ confirmPassword: 'Passwords do not match.' });
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          emailRedirectTo: `${process.env.REACT_APP_SITE_URL || window.location.origin}/auth/callback`,
          data: {
            full_name: trimmedName,
            role: 'customer',
            marketing_opt_in: marketingOptIn,
            accepted_terms_at: new Date().toISOString(),
          },
        },
      });

      if (authError) throw authError;

      // Supabase returns success with an empty identities list when the email already exists,
      // and does not send another confirmation email.
      const identities = authData?.user?.identities;
      if (!authData?.user || !Array.isArray(identities) || identities.length === 0) {
        throw new Error('Email already registered');
      }

      setIsSent(true);
    } catch (err) {
      console.error('Signup Error:', err.message);
      const normalizedMessage = (err.message || '').toLowerCase();

      if (
        normalizedMessage.includes('already registered') ||
        normalizedMessage.includes('user already exists') ||
        normalizedMessage.includes('duplicate')
      ) {
        setFieldErrors({ email: 'Email already registered' });
        setError('Email already registered');
      } else if (normalizedMessage.includes('invalid email')) {
        setFieldErrors({ email: 'Invalid email format' });
        setError('Invalid email format');
      } else if (normalizedMessage.includes('weak password')) {
        setFieldErrors({ password: 'Password must meet strength requirements.' });
        setError('Password must be at least 8 characters and include uppercase, lowercase, number, and special character.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (isSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f2ef] px-6">
        <div className="max-w-md w-full bg-white p-10 rounded-[2.5rem] shadow-2xl text-center border border-[#dde2ea]">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mb-6 inline-flex items-center gap-2 text-xs font-black uppercase tracking-wide text-[#5f6b7d] hover:text-[#0f2746] transition"
          >
            <ArrowLeft size={14} /> Back to Home
          </button>

          <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={40} />
          </div>
          <h1 className="text-2xl font-black text-[#0f2746] mb-4 tracking-tighter italic uppercase">Verification Sent</h1>
          <p className="text-sm text-[#5f6b7d] mb-8 leading-relaxed">
            A link has been sent to <span className="font-bold text-[#0f2746]">{formData.email}</span>. Please verify your email before attempting to log in.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-[#8b0000] text-white py-4 rounded-full text-xs font-black uppercase tracking-[0.18em] hover:bg-[#6f0000] transition shadow-lg"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#f4f2ef]">
      <div className="hidden md:flex md:w-1/2 bg-black items-center justify-center p-12">
        <img
          src="/assets/echojham_logos/echojham.logo3.png"
          alt="Echo Jham"
          className="w-[280px] max-w-full h-auto select-none"
        />
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center px-6 py-10 md:px-12">
        <div className="w-full max-w-[420px]">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mb-6 inline-flex items-center gap-2 text-xs font-black uppercase tracking-wide text-[#5f6b7d] hover:text-[#0f2746] transition"
          >
            <ArrowLeft size={14} /> Back to Home
          </button>

          <h1 className="text-3xl md:text-[2.15rem] font-black text-[#0f2746] tracking-tighter uppercase italic mb-2 leading-none">Sign Up</h1>
          <p className="text-sm text-[#5f6b7d] font-medium mb-10">Create an account to continue.</p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-600 text-red-700 text-[11px] font-bold rounded-r-xl flex items-center gap-3 shadow-sm">
              <AlertCircle size={18} className="shrink-0" /> {error}
            </div>
          )}

          <form onSubmit={handleSignup} noValidate className="space-y-4">
            <Input
              icon={<User size={16} />}
              placeholder="Full Name"
              name="fullName"
              type="text"
              value={formData.fullName}
              onChange={handleChange}
              errorText={fieldErrors.fullName}
            />
            <Input
              icon={<Mail size={16} />}
              placeholder="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              errorText={fieldErrors.email}
            />

            <Input
              icon={<Lock size={16} />}
              placeholder="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              isPasswordField
              showPass={showPassword}
              togglePass={() => setShowPassword(!showPassword)}
              errorText={fieldErrors.password}
              helperText="Use at least 8 characters with uppercase, lowercase, number, and special character."
            />

            <Input
              icon={<Lock size={16} />}
              placeholder="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              isPasswordField
              showPass={showConfirmPassword}
              togglePass={() => setShowConfirmPassword(!showConfirmPassword)}
              errorText={fieldErrors.confirmPassword}
            />

            <LegalConsentFields
              acceptedLegal={acceptedLegal}
              errorText={fieldErrors.acceptedLegal}
              onRevokeLegal={() => handleAcceptedLegalChange(false)}
              onOpenTerms={() => openLegalModal('terms')}
              onOpenPrivacy={() => openLegalModal('privacy')}
            />

            <button
              disabled={loading}
              className="w-full mt-6 bg-[#8b0000] hover:bg-[#6f0000] text-white py-4 rounded-full text-xs font-black tracking-[0.18em] uppercase shadow-[0_16px_30px_rgba(139,0,0,0.18)] transition active:scale-95 disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : 'Register Now'}
            </button>
          </form>

          <p className="text-center text-sm font-medium text-[#5f6b7d] mt-10">
            Joined Already?{' '}
            <Link to="/login" className="ml-2 text-[#8b0000] font-black hover:text-[#0f2746] italic">
              Sign In
            </Link>
          </p>
        </div>
      </div>

      <LegalDocumentsModal
        open={legalModalOpen}
        initialSection={legalModalSection}
        logoSrc="/assets/echojham_logos/echojham.logo2.png"
        initialAcceptedLegal={acceptedLegal}
        initialMarketingOptIn={marketingOptIn}
        onDecline={() => setLegalModalOpen(false)}
        onAccept={({ marketingOptIn: nextMarketingOptIn }) => {
          handleAcceptedLegalChange(true);
          setMarketingOptIn(Boolean(nextMarketingOptIn));
          setLegalModalOpen(false);
        }}
      />
    </div>
  );
};

/* --- Sub-Component for Clean Code --- */
const Input = ({ icon, isPasswordField, showPass, togglePass, ...props }) => (
  <div className="group">
    {(() => {
      const { errorText, helperText, ...inputProps } = props;
      return (
        <>
          <div className="relative">
            <span className="absolute left-5 top-1/2 -translate-y-1/2 text-[#9ba3b3] group-focus-within:text-[#8b0000] transition-colors">
              {icon}
            </span>
            <input
              {...inputProps}
              aria-invalid={Boolean(errorText)}
              className={`w-full pl-14 pr-12 py-4 rounded-2xl border bg-white text-sm focus:outline-none focus:ring-2 transition-all text-[#0f2746] placeholder-[#a8b0c0] shadow-sm ${
                errorText
                  ? 'border-red-500/50 focus:ring-red-500/20 focus:border-red-500'
                  : 'border-[#d8dee8] focus:ring-[#8b0000]/15 focus:border-[#8b0000]'
              }`}
            />
            {isPasswordField && (
              <button
                type="button"
                onClick={togglePass}
                className="absolute right-5 top-1/2 -translate-y-1/2 text-[#9ba3b3] hover:text-[#0f2746] transition-colors"
              >
                {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            )}
          </div>
          {errorText ? <p className="mt-1 text-[11px] font-semibold text-red-600">{errorText}</p> : null}
          {!errorText && helperText ? <p className="mt-1 text-[11px] text-[#5f6b7d]">{helperText}</p> : null}
        </>
      );
    })()}
  </div>
);

export default SignupPage;
